import PlayArrowOutlined from "@mui/icons-material/PlayArrowOutlined";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useEffect, useMemo, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { getEmployerDossier } from "../../api/dossier/getEmployerDossier";
import { terminateEmployerDossier } from "../../api/dossier/terminateEmployerDossier";
import APContactHelpButton from "../../components/buttons/APContactHelpButton";
import APIcon from "../../components/buttons/APIcon";
import BackButton from "../../components/buttons/BackButton";
import InfoButton from "../../components/buttons/InfoButton";
import FaqList from "../../components/faq/FaqList";
import APAutoComplete, {
  AutoCompleteOption,
} from "../../components/form/APAutoComplete";
import APCheckbox from "../../components/form/APCheckbox";
import APDateField from "../../components/form/APDateField";
import APForm from "../../components/form/APForm";
import APRadioGroup, {
  RadioGroupOption,
} from "../../components/form/APRadioGroup";
import APTextField from "../../components/form/APTextField";
import PageContainer from "../../components/PageContainer";
import {
  filterAccountsByPermission,
  hasPermissionForAccount,
} from "../../helpers/user";
import { useFaqs } from "../../hooks/useFaqs";
import { useAuth } from "../../providers/AuthProvider";
import {
  DossierTerminationReason,
  DossierTerminationReasonLabelMap,
} from "../../types/Dossier";
import { User } from "../../types/User";
import DossierSummary from "../components/DossierSummary";
import DossierTerminationSummary from "../components/DossierTerminationSummary";

type TerminateDossierInputs = {
  lastDateOfEmployment: string | null;
  targetLastPayrollDate: string | null;
  terminationReason: AutoCompleteOption | null;
  leavingEmployer: "yes" | "no" | null;
  driverIsNotified: "yes" | "no" | null;
  noteFromEmployer: string;
  confirm: boolean;
};

const leavingEmployerRadioOptions: {
  id: string;
  label: string;
}[] = [
  { id: "yes", label: "Yes" },
  { id: "no", label: "No" },
];

const driverIsNotifiedRadioOptions: RadioGroupOption[] = [
  {
    id: "yes",
    label: "Yes",
    belowField: (
      <Box sx={{ display: "flex", alignItems: "start", columnGap: 1 }} ml={4}>
        <APIcon name="warning" color="warning" size="small" />
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="labelLarge">
            Employee will be contacted to progress the termination.
          </Typography>
        </Box>
      </Box>
    ),
  },
  { id: "no", label: "No" },
];

const terminationReasonOptions: AutoCompleteOption[] = [
  {
    id: "employer-terminated-employment",
    label: DossierTerminationReasonLabelMap["employer-terminated-employment"],
  },
  {
    id: "employee-terminated-employment",
    label: DossierTerminationReasonLabelMap["employee-terminated-employment"],
  },
  {
    id: "other",
    label: DossierTerminationReasonLabelMap["other"],
  },
];

const EmployerTerminateDossier = ({ me }: { me: User }) => {
  const { dossierId } = useParams();
  const navigate = useNavigate();
  const [navigationCheckComplete, setNavigationCheck] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const { getScopedPageUrl } = useAuth();

  const {
    control,
    handleSubmit,
    setValue,
    setError,
    getValues,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<TerminateDossierInputs>({ mode: "onTouched" });

  const terminationReason = watch("terminationReason");
  const leavingEmployer = watch("leavingEmployer");

  if (!dossierId) {
    // TODO: improve client side error messaging
    throw new Error("dossier id param is misisng");
  }

  const authorisedAccounts = me.accountPermissions
    ? filterAccountsByPermission(me.accountPermissions, "terminateDossiers")
    : [];

  const dossier = getEmployerDossier(dossierId);

  const {
    faqProps,
    isLoading: isLoadingFaqs,
    isSuccess: isSuccessFaqs,
  } = useFaqs({
    pageId: "employer-request-lease-closure",
    contextIds: {
      opportunityId: dossier?.data?.opportunity?.id,
    },
  });

  const { mutateAsync, isLoading: isSubmitting } = terminateEmployerDossier(
    dossierId,
    {
      mutationOptions: {
        successNavPath: getScopedPageUrl(
          "employerViewDossierTermination",
          dossierId
        ),
        successReplaceHistory: true,
        successAlertMessage: "Termination commenced.",
      },
    }
  );

  if (dossier.isError) {
    throw dossier.error;
  }

  const isLoading = dossier.isLoading || isLoadingFaqs || isSubmitting;
  const isSuccess = dossier.isSuccess && isSuccessFaqs;

  useEffect(() => {
    if (isSuccess) {
      if (
        authorisedAccounts.length > 0 &&
        dossier.data?.opportunity &&
        hasPermissionForAccount(
          me.accountPermissions,
          "terminateDossiers",
          dossier.data.opportunity.parentAccountId ?? ""
        )
      ) {
        if (dossier.data.status === "Active") {
          setNavigationCheck(true);
        } else if (dossier.data.terminationId) {
          navigate(
            getScopedPageUrl("employerViewDossierTermination", dossierId),
            { replace: true }
          );
        } else {
          navigate(getScopedPageUrl("employerHome"), { replace: true });
        }
      } else {
        navigate(getScopedPageUrl("employerHome"), { replace: true });
      }
    }
  }, [isSuccess]);

  useEffect(() => {
    setValue("driverIsNotified", null);
    setValue("leavingEmployer", null);
  }, [terminationReason]);

  useEffect(() => {
    setValue("lastDateOfEmployment", null);
    setValue("targetLastPayrollDate", null);
  }, [terminationReason, leavingEmployer]);

  const onSubmit: SubmitHandler<TerminateDossierInputs> = async (data) => {
    if (!isConfirming) {
      setIsConfirming(true);
      window.scrollTo(0, 0);
      return;
    }

    let driverIsNotified = null;
    let leavingEmployer = null;

    if (data.driverIsNotified === "yes") {
      driverIsNotified = true;
    } else if (data.driverIsNotified === "no") {
      driverIsNotified = false;
    }
    if (data.leavingEmployer === "yes") {
      leavingEmployer = true;
    } else if (data.leavingEmployer === "no") {
      leavingEmployer = false;
    }

    if (data.terminationReason) {
      await mutateAsync({
        lastDateOfEmployment: data.lastDateOfEmployment,
        targetLastPayrollDate: data.targetLastPayrollDate,
        terminationReason: data.terminationReason
          .id as DossierTerminationReason,
        driverNotifiedOfTermination: driverIsNotified,
        leavingEmployer: leavingEmployer,
        noteFromEmployer: data.noteFromEmployer ?? null,
      });
    }
  };

  const onConfirmBack = () => {
    setIsConfirming(false);
    setValue("confirm", false);
    window.scrollTo(0, 0);
  };

  const terminationSummary = useMemo(() => {
    if (isConfirming) {
      const values = getValues();
      let leavingEmployer = null;
      let driverNotifiedOfTermination = null;

      if (values.leavingEmployer === "yes") {
        leavingEmployer = true;
      } else if (values.leavingEmployer === "no") {
        leavingEmployer = false;
      }

      if (values.driverIsNotified === "yes") {
        driverNotifiedOfTermination = true;
      } else if (values.driverIsNotified === "no") {
        driverNotifiedOfTermination = false;
      }

      return (
        <DossierTerminationSummary
          dossierTermination={{
            leavingEmployer: leavingEmployer ?? undefined,
            lastDateOfEmployment: values.lastDateOfEmployment,
            targetLastPayrollDate: values.targetLastPayrollDate,
            terminationReason: values.terminationReason
              ?.id as DossierTerminationReason,
            driverNotifiedOfTermination:
              driverNotifiedOfTermination ?? undefined,
            noteFromEmployer: values.noteFromEmployer ?? undefined,
          }}
          displayedFields={[
            "leavingEmployer",
            "terminationReason",
            "lastDateOfEmployment",
            "targetLastPayrollDate",
            "driverNotifiedOfTermination",
            "noteFromEmployer",
          ]}
        />
      );
    }

    return <></>;
  }, [isConfirming]);

  const renderAppLocation = faqProps.renderAppLocation;
  const showIsLeavingEmployer = useMemo(() => {
    return terminationReason?.id === "other";
  }, [terminationReason]);
  const showTerminationDates = useMemo(() => {
    return (
      terminationReason &&
      (terminationReason?.id !== "other" || leavingEmployer === "yes")
    );
  }, [terminationReason, leavingEmployer]);
  const showDriverIsNotified = useMemo(() => {
    return (
      terminationReason &&
      (terminationReason?.id !== "other" || leavingEmployer)
    );
  }, [terminationReason, leavingEmployer]);
  const requiredNoteFromEmployer = useMemo(() => {
    return terminationReason?.id === "other";
  }, [terminationReason]);

  return (
    <PageContainer
      title={isConfirming ? "Confirm Termination" : "Request Termination"}
      titleButton={
        isSuccess ? (
          <Box mr={1}>
            {renderAppLocation(
              "EmployerRequestClose-Page",
              <InfoButton />,
              true,
              true
            )}
          </Box>
        ) : undefined
      }
      loading={isLoading || !navigationCheckComplete}
    >
      {isSuccess && navigationCheckComplete && (
        <Stack spacing={2}>
          <Typography variant="titleMedium">
            {isConfirming
              ? "Please confirm that you wish to initiate the termination of the following novated lease."
              : "Please complete the following to initiate termination."}
          </Typography>

          <DossierSummary
            dossier={dossier.data}
            displayedFields={[
              "employee",
              "employer",
              "contractId",
              "startDate",
              "vehicle",
            ]}
          />

          <APForm
            onSubmit={handleSubmit(onSubmit)}
            submitText={isConfirming ? "Submit" : "Next"}
            submitIcon={<PlayArrowOutlined />}
            leftButton={
              <Stack
                spacing={2}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <BackButton
                  onClick={isConfirming ? onConfirmBack : undefined}
                />
                <APContactHelpButton
                  regardingOpportunityId={
                    dossier.data.opportunity?.id ?? undefined
                  }
                />
              </Stack>
            }
            isLoading={isSubmitting}
            isError={Object.keys(errors).length > 0}
          >
            {isConfirming && terminationSummary}

            <APAutoComplete
              name="terminationReason"
              label="Termination Reason"
              options={terminationReasonOptions}
              control={control}
              validations={{ required: true }}
              errors={errors}
              defaultValue={null}
              hidden={isConfirming}
            />

            {showIsLeavingEmployer && !isConfirming && (
              <Box>
                <APRadioGroup
                  name="leavingEmployer"
                  label="Employee leaving this Employer?"
                  validations={{ required: true }}
                  errors={errors}
                  control={control}
                  defaultValue={null}
                  options={leavingEmployerRadioOptions}
                  hidden={isConfirming}
                />
              </Box>
            )}

            {showTerminationDates && (
              <APDateField
                name="lastDateOfEmployment"
                label="Last Date of Employment"
                control={control}
                defaultValue={null}
                errors={errors}
                setError={setError}
                clearErrors={clearErrors}
                validations={{ required: true }}
                dateOnlyFormat={true}
                hidden={isConfirming}
              />
            )}

            {showTerminationDates && (
              <APDateField
                name="targetLastPayrollDate"
                label="Target Last Payroll Date"
                control={control}
                defaultValue={null}
                errors={errors}
                setError={setError}
                clearErrors={clearErrors}
                validations={{ required: true }}
                dateOnlyFormat={true}
                hidden={isConfirming}
              />
            )}

            {showDriverIsNotified && !isConfirming && (
              <Box>
                <APRadioGroup
                  name="driverIsNotified"
                  label="Employee Notified of Termination?"
                  validations={{ required: true }}
                  errors={errors}
                  control={control}
                  defaultValue={null}
                  options={driverIsNotifiedRadioOptions}
                  hidden={isConfirming}
                />
              </Box>
            )}

            <APTextField<TerminateDossierInputs>
              name="noteFromEmployer"
              label="Note"
              control={control}
              validations={{ required: requiredNoteFromEmployer }}
              errors={errors}
              defaultValue=""
              multiline
              minRows={1}
              hidden={isConfirming}
            />

            {isConfirming && (
              <APCheckbox
                name="confirm"
                control={control}
                label="Please initiate the termination of this novated lease. This will notify payroll."
                defaultValue={false}
                validations={{ required: true }}
                errors={errors}
                hideRequired
              />
            )}

            {!isConfirming && (
              <FaqList
                pageId="employer-request-lease-closure"
                appLocationIdentifier="EmployerRequestClose-FAQList"
                faqContextIds={{
                  opportunityId: dossier.data.opportunity?.id ?? undefined,
                }}
              />
            )}
          </APForm>
        </Stack>
      )}
    </PageContainer>
  );
};

export default EmployerTerminateDossier;
